import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { peso } from "../lib/format";
import { Btn, Money, cn } from "../components/ui/kit";

const CHANNELS = ["DINE-IN", "TAKEOUT", "DELIVERY", "ONLINE"];
const SERVICE = ["Water", "Utensils", "Tissue", "Call Waiter", "Request Bill"];

export default function CustomerApp() {
  const { data: menu } = usePoll(() => api.get("/menu").then((r) => r.data), 0);
  const [channel, setChannel] = useState("DINE-IN");
  const [table, setTable] = useState("T1");
  const [cat, setCat] = useState(null);
  const [cart, setCart] = useState([]);
  const [active, setActive] = useState(null); // item modal
  const [cartOpen, setCartOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [mobile, setMobile] = useState("");
  const [placed, setPlaced] = useState(null);
  const [payMethod, setPayMethod] = useState("GCash");

  const cats = menu?.categories || [];
  const items = menu?.items || [];
  const shown = items.filter((i) => (cat ? i.category === cat : true));
  const total = useMemo(
    () => cart.reduce((s, l) => s + (l.unit_price + l.modifiers.reduce((a, m) => a + (m.price || 0), 0)) * l.qty, 0),
    [cart]
  );
  const count = cart.reduce((s, l) => s + l.qty, 0);

  const addToCart = (line) => {
    setCart((c) => [...c, line]);
    setActive(null);
    toast.success(`${line.name} added`);
  };
  const removeLine = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  const identify = async () => {
    try {
      const { data } = await api.get(`/customers/lookup`, { params: { mobile } });
      setCustomer(data);
      toast.success(`Welcome back, ${data.name.split(" ")[0]}!`);
    } catch (e) {
      toast.error("No profile found — continuing as guest");
    }
  };

  const submit = async () => {
    if (!cart.length) return;
    try {
      const payload = {
        channel,
        table: channel === "DINE-IN" ? table : null,
        customer_id: customer?.id || null,
        customer_name: customer?.name || null,
        customer_mobile: customer?.mobile || mobile || null,
        delivery_info: ["DELIVERY", "ONLINE"].includes(channel) ? { address: "123 Sample St.", note: "Leave at gate" } : null,
        items: cart.map((l) => ({
          menu_item_id: l.menu_item_id, name: l.name, qty: l.qty,
          unit_price: l.unit_price, modifiers: l.modifiers, special_instructions: l.special_instructions || "",
        })),
      };
      const { data: order } = await api.post("/orders", payload);
      if (["ONLINE", "DELIVERY"].includes(channel)) {
        await api.post(`/orders/${order.id}/pay`, { amount: order.bill.total, method: payMethod, type: "full" });
      }
      setPlaced(order);
      setCart([]);
      setCartOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const serviceReq = async (type) => {
    try {
      await api.post("/service-requests", { table, type, customer_name: customer?.name });
      toast.success(`${type} — a team member is on the way`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (placed) return <Placed order={placed} channel={channel} onNew={() => setPlaced(null)} />;

  return (
    <div className="min-h-[calc(100vh-49px)] bg-amber-50/60 text-zinc-900">
      <div className="mx-auto max-w-[1400px] px-4 py-6">
        {/* header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-head text-3xl font-black tracking-tight">Kusina Filipiniana</h1>
            <p className="text-sm text-emerald-700">Every visit creates value — browse, customize, and send to your server.</p>
          </div>
          <div className="flex items-center gap-2">
            {CHANNELS.map((c) => (
              <button key={c} data-testid={`channel-${c}`} onClick={() => setChannel(c)}
                className={cn("rounded-full px-3 py-1.5 text-xs font-bold", channel === c ? "bg-emerald-600 text-white" : "bg-white text-zinc-600 border border-zinc-200")}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* identify + table */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {channel === "DINE-IN" && (
            <div className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm">
              <span className="text-zinc-400">Table</span>
              <input value={table} onChange={(e) => setTable(e.target.value)} className="w-14 bg-transparent font-bold outline-none" data-testid="table-input" />
            </div>
          )}
          {!customer ? (
            <div className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm">
              <input placeholder="Mobile # (optional)" value={mobile} onChange={(e) => setMobile(e.target.value)}
                className="w-40 bg-transparent outline-none" data-testid="mobile-input" />
              <button onClick={identify} className="font-bold text-emerald-600" data-testid="identify-btn">Identify</button>
            </div>
          ) : (
            <LoyaltyCard customer={customer} />
          )}
        </div>

        {/* categories */}
        <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <Cat label="All" active={!cat} onClick={() => setCat(null)} />
          {cats.map((c) => <Cat key={c} label={c} active={cat === c} onClick={() => setCat(c)} />)}
        </div>

        {/* menu grid */}
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {shown.map((it) => (
            <button key={it.id} data-testid={`menu-item-${it.id}`} onClick={() => it.available && setActive(it)}
              className={cn("group overflow-hidden rounded-2xl border border-zinc-200 bg-white text-left shadow-sm transition hover:shadow-md", !it.available && "opacity-50")}>
              <div className="aspect-[4/3] overflow-hidden bg-zinc-100">
                <img src={it.image} alt={it.name} className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-head text-sm font-bold leading-tight">{it.name}</h3>
                  <Money value={it.price} className="text-sm font-bold text-emerald-700" />
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{it.description}</p>
                {!it.available && <span className="mt-1 inline-block text-[10px] font-bold text-rose-500">UNAVAILABLE</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* service requests dock */}
      <div className="fixed bottom-4 left-1/2 z-30 flex -translate-x-1/2 gap-2 rounded-full border border-zinc-200 bg-white/90 p-1.5 shadow-lg glass">
        {SERVICE.map((s) => (
          <button key={s} data-testid={`service-${s.replace(/\s/g, "-").toLowerCase()}`} onClick={() => serviceReq(s)}
            className="rounded-full px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-emerald-50 hover:text-emerald-700">{s}</button>
        ))}
      </div>

      {/* cart button */}
      {count > 0 && (
        <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)}
          className="fixed bottom-20 right-4 z-30 flex items-center gap-3 rounded-full bg-zinc-900 px-5 py-3 font-bold text-white shadow-xl">
          <span>🛒 {count}</span><Money value={total} />
        </button>
      )}

      {active && <ItemModal item={active} onClose={() => setActive(null)} onAdd={addToCart} />}
      {cartOpen && (
        <CartDrawer cart={cart} total={total} channel={channel} payMethod={payMethod} setPayMethod={setPayMethod}
          onRemove={removeLine} onClose={() => setCartOpen(false)} onSubmit={submit} />
      )}
    </div>
  );
}

function Cat({ label, active, onClick }) {
  return (
    <button onClick={onClick} className={cn("whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold", active ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200")}>{label}</button>
  );
}

function LoyaltyCard({ customer }) {
  const remaining = Math.max(0, 2 - (customer.visit_count % 3));
  return (
    <div data-testid="loyalty-card" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-600 to-emerald-700 px-4 py-2 text-white">
      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Today's Value · Visit #{customer.visit_count + 1}</div>
      <div className="flex items-center gap-4">
        <div className="font-head text-lg font-black">{customer.name.split(" ")[0]}</div>
        <div className="text-sm"><b>{customer.points}</b> pts</div>
        <div className="text-xs text-emerald-100">Free dessert in {remaining} more visits</div>
      </div>
    </div>
  );
}

function ItemModal({ item, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [mods, setMods] = useState([]);
  const [note, setNote] = useState("");
  const toggle = (m) => setMods((x) => (x.find((y) => y.name === m.name) ? x.filter((y) => y.name !== m.name) : [...x, m]));
  const extra = mods.reduce((a, m) => a + (m.price || 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl rc-rise" onClick={(e) => e.stopPropagation()}>
        <img src={item.image} alt={item.name} className="h-44 w-full object-cover" />
        <div className="p-5">
          <div className="flex items-start justify-between">
            <h3 className="font-head text-xl font-black">{item.name}</h3>
            <Money value={item.price} className="font-bold text-emerald-700" />
          </div>
          <p className="mt-1 text-sm text-zinc-500">{item.description}</p>
          <div className="mt-4 text-xs font-bold uppercase tracking-wider text-zinc-400">Customize</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(item.modifiers || []).map((m) => (
              <button key={m.name} data-testid={`mod-${m.name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => toggle(m)}
                className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", mods.find((y) => y.name === m.name) ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600")}>
                {m.name}{m.price ? ` +${peso(m.price)}` : ""}
              </button>
            ))}
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Special instructions…"
            className="mt-4 w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-emerald-500" data-testid="special-instructions" />
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-9 w-9 rounded-full border border-zinc-300 text-lg font-bold">−</button>
              <span className="w-6 text-center font-bold" data-testid="qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="h-9 w-9 rounded-full border border-zinc-300 text-lg font-bold">+</button>
            </div>
            <Btn variant="emerald" data-testid="add-to-cart-btn"
              onClick={() => onAdd({ menu_item_id: item.id, name: item.name, unit_price: item.price, qty, modifiers: mods, special_instructions: note })}>
              Add · {peso((item.price + extra) * qty)}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function CartDrawer({ cart, total, channel, payMethod, setPayMethod, onRemove, onClose, onSubmit }) {
  const methods = ["GCash", "Maya", "Credit Card", "Cash on Delivery"];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col bg-white rc-rise" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-head text-lg font-black">Your Order · {channel}</h3>
          <button onClick={onClose} className="text-zinc-400">✕</button>
        </div>
        <div className="flex-1 space-y-3 overflow-auto p-4">
          {cart.map((l, i) => (
            <div key={i} className="flex items-start justify-between gap-3 rounded-xl border border-zinc-100 p-3">
              <div>
                <div className="font-bold">{l.qty}× {l.name}</div>
                {l.modifiers.length > 0 && <div className="text-xs text-emerald-700">{l.modifiers.map((m) => m.name).join(", ")}</div>}
                {l.special_instructions && <div className="text-xs italic text-zinc-500">“{l.special_instructions}”</div>}
              </div>
              <div className="text-right">
                <Money value={(l.unit_price + l.modifiers.reduce((a, m) => a + (m.price || 0), 0)) * l.qty} className="text-sm font-bold" />
                <button onClick={() => onRemove(i)} className="block text-xs text-rose-500">remove</button>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t p-4">
          {["ONLINE", "DELIVERY"].includes(channel) && (
            <div className="mb-3">
              <div className="mb-1 text-xs font-bold uppercase tracking-wider text-zinc-400">Payment</div>
              <div className="flex flex-wrap gap-2">
                {methods.map((m) => (
                  <button key={m} onClick={() => setPayMethod(m)} data-testid={`pay-${m.replace(/\s/g, "-").toLowerCase()}`}
                    className={cn("rounded-full border px-3 py-1 text-xs font-semibold", payMethod === m ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600")}>{m}</button>
                ))}
              </div>
            </div>
          )}
          <div className="mb-3 flex items-center justify-between font-head text-lg font-black">
            <span>Total</span><Money value={total} className="text-emerald-700" />
          </div>
          <Btn variant="emerald" className="w-full py-3" onClick={onSubmit} data-testid="submit-order-btn">
            {["ONLINE", "DELIVERY"].includes(channel) ? "Pay & Submit Order" : "Send to Waiter"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function Placed({ order, channel, onNew }) {
  return (
    <div className="grid min-h-[calc(100vh-49px)] place-items-center bg-amber-50/60 p-6 text-center text-zinc-900">
      <div className="max-w-md rc-pop">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-100 text-4xl">✓</div>
        <h2 className="mt-4 font-head text-2xl font-black">Order {order.order_number} sent!</h2>
        <p className="mt-2 text-emerald-700">
          {["ONLINE", "DELIVERY"].includes(channel)
            ? "Payment received. Your order is queued for kitchen once confirmed."
            : "Status: AWAITING WAITER CONFIRMATION. Your server will confirm shortly."}
        </p>
        <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4 text-left">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm"><span>{it.qty}× {it.name}</span><Money value={it.unit_price * it.qty} /></div>
          ))}
          <div className="mt-2 flex justify-between border-t pt-2 font-bold"><span>Total</span><Money value={order.bill.total} /></div>
        </div>
        <Btn className="mt-5" variant="emerald" onClick={onNew} data-testid="new-order-btn">Place another order</Btn>
      </div>
    </div>
  );
}
